import { ArrayUtil } from 'helper-util';
import { ISecurity } from '../../pages/SwapsTek/OrderDetails/OrderDetailsSlice';
import {
  MIN_SEARCH_LENGTH,
  SEARCHABLE_FIELDS,
  type BasketUploadRowContext,
  type CsvBasketRow,
  type PendingApiSecurity,
  type SecurityEnrichmentOptions,
  type SecurityIdentifierField,
  type SecurityInput,
  type SkippedUploadRow,
} from './securityMasterCache.model';

const trimValue = (value?: string): string => (value ?? '').trim();

const toNullableIdentifier = (value?: string): string | null => {
  const trimmed = trimValue(value);
  return trimmed || null;
};

export const normalizeKeyword = (keyword: string): string => keyword.trim().toUpperCase();

export const toGraphqlKeywordLiteral = (normalizedKeyword: string): string =>
  normalizedKeyword.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const isSearchableKeyword = (keyword: string): boolean =>
  normalizeKeyword(keyword).length >= MIN_SEARCH_LENGTH;

const fieldValue = (security: ISecurity, field: SecurityIdentifierField): string => {
  const value = security[field];
  return typeof value === 'string' ? value.trim() : '';
};

export const securityMatchesKeyword = (security: ISecurity, keyword: string): boolean => {
  const normalizedKeyword = normalizeKeyword(keyword);
  if (!normalizedKeyword) return false;

  return SEARCHABLE_FIELDS.some((field) => {
    const value = fieldValue(security, field);
    if (!value) return false;
    return value.toUpperCase().includes(normalizedKeyword);
  });
};

export const filterSecuritiesFromCache = (cache: ISecurity[], keyword: string): ISecurity[] => {
  if (normalizeKeyword(keyword).length < MIN_SEARCH_LENGTH) return [];
  return cache.filter((security) => securityMatchesKeyword(security, keyword));
};

export const getDropdownValues = (
  securities: ISecurity[],
  field: SecurityIdentifierField,
): string[] => {
  const seen = new Set<string>();
  const values: string[] = [];

  securities.forEach((security) => {
    const raw = security[field];
    if (!raw) return;
    const value = field === 'securityName' ? String(raw).trim() : String(raw).trim();
    if (!value || seen.has(value)) return;
    if (field === 'securityName') {
      seen.add(`${value} | ${security.assetClass} | ${security.sedol} | ${security.bnySecurityId}`);
      values.push(
        `${value} | ${security.assetClass} | ${security.sedol} | ${security.bnySecurityId}`,
      );
    } else if (field === 'sedol') {
      seen.add(
        `${value} | ${security.assetClass} | ${security.securityName}| ${security.bnySecurityId}`,
      );
      values.push(
        `${value} | ${security.assetClass} | ${security.securityName}| ${security.bnySecurityId}`,
      );
    } else {
      seen.add(
        `${value} | ${security.assetClass} | ${security.securityName} | ${security.sedol} | ${security.bnySecurityId}`,
      );
      values.push(
        `${value} | ${security.assetClass} | ${security.securityName} | ${security.sedol} | ${security.bnySecurityId}`,
      );
    }
  });

  return values;
};

export const isNegativeCacheHit = (emptyKeywords: string[], keyword: string): boolean => {
  const normalized = normalizeKeyword(keyword);
  return emptyKeywords.some((k) => k === normalized);
};

export const mergeSecurities = (existing: ISecurity[], incoming: ISecurity[]): ISecurity[] => {
  const merged = [...existing];
  const existingIds = new Set(existing.map((s) => s.bnySecurityId));

  incoming.forEach((security) => {
    if (!security?.bnySecurityId || existingIds.has(security.bnySecurityId)) return;
    merged.push(security);
    existingIds.add(security.bnySecurityId);
  });

  return merged;
};

export const findSecurityByFieldValue = (
  cache: ISecurity[],
  field: SecurityIdentifierField,
  value: string,
): ISecurity | undefined => {
  if (!value) return undefined;
  const splittedValue = value.split('|');

  if (ArrayUtil.isEmpty(splittedValue)) return undefined;

  const firstValue = splittedValue[0]?.trim();
  const assetClass = splittedValue[1]?.trim().toUpperCase();
  const selectedBnySecurityId = splittedValue[splittedValue.length - 1]?.trim();

  const normalizedValue =
    field === 'securityName' ? firstValue.trim() : firstValue.trim().toUpperCase();

  return cache.find((security) => {
    const candidate = fieldValue(security, field);
    if (!candidate) return false;
    if (field === 'securityName') {
      return (
        candidate === normalizedValue && security?.assetClass?.trim()?.toUpperCase() === assetClass
      );
    }
    return (
      candidate.toUpperCase() === normalizedValue &&
      security?.assetClass?.trim()?.toUpperCase() === assetClass &&
      security?.bnySecurityId?.trim() === selectedBnySecurityId
    );
  });
};

export const mapSecurityToEnrichedFields = (
  security: ISecurity,
  options?: SecurityEnrichmentOptions,
) => {
  const issueCurrency = trimValue(security.issueCurrency);
  const fxRate =
    options?.swapCurrency && issueCurrency === options.swapCurrency ? 1 : options?.existingFxRate;

  return {
    bnySecurityId: security.bnySecurityId,
    ticker: security.ticker,
    isin: security.isin,
    sedol: security.sedol,
    cusip: security.cusip,
    securityName: security.securityName,
    securityDescription: security.securityName,
    currency: issueCurrency,
    assetClass: security.assetClass,
    ...(fxRate !== undefined ? { fxRate } : {}),
  };
};

export const emptySecurityEnrichedFields = () => ({
  bnySecurityId: '',
  isin: '',
  sedol: '',
  cusip: '',
  securityDescription: '',
  currency: '',
});

export const resolveSecurityByField = (
  cache: ISecurity[],
  field: SecurityIdentifierField,
  value: string,
  options?: SecurityEnrichmentOptions,
) => {
  const security = findSecurityByFieldValue(cache, field, value);
  return security ? mapSecurityToEnrichedFields(security, options) : null;
};

export const resolveSecurityFieldValues = async (
  field: SecurityIdentifierField,
  search: string,
  cache: ISecurity[],
  emptyKeywords: string[],
  fetchSecurities: (keyword: string) => Promise<ISecurity[]>,
): Promise<string[]> => {
  const keyword = normalizeKeyword(search);
  const cached = filterSecuritiesFromCache(cache, keyword);
  if (cached.length > 0) {
    return getDropdownValues(cached, field);
  }

  if (isNegativeCacheHit(emptyKeywords, keyword)) {
    return [];
  }

  const incoming = await fetchSecurities(keyword);
  return getDropdownValues(incoming, field);
};

export const mapEnrichedRowToSubmitSecurityFields = (row: {
  bnySecurityId: string;
  ticker: string;
  securityDescription: string;
  isin: string;
  sedol: string;
  currency: string;
}) => ({
  security: row.bnySecurityId,
  bbgTickerExch: row.ticker,
  securityName: row.securityDescription,
  isin: row.isin,
  sedol: row.sedol,
  currency: row.currency,
  securityIssueCountry: row.currency,
});

export const toSecurityInput = (row: CsvBasketRow): SecurityInput => ({
  ticker: toNullableIdentifier(row.SecurityID),
  cusip: toNullableIdentifier(row.Cusip),
  isin: toNullableIdentifier(row.Isin),
  sedol: toNullableIdentifier(row.Sedol),
  assetClass: toNullableIdentifier(row.AssetClass),
});

export const hasAnyIdentifier = (input: SecurityInput): boolean =>
  !!(input.ticker || input.cusip || input.isin || input.sedol);

export const buildSecurityInputKey = (input: SecurityInput): string =>
  [input.ticker, input.cusip, input.isin, input.sedol, input.assetClass]
    .map((value) => (value ? normalizeKeyword(value) : ''))
    .join('|');

export const getCsvRowIdentifierLabel = (row: CsvBasketRow): string => {
  const ticker = trimValue(row.SecurityID);
  const isin = trimValue(row.Isin);
  const sedol = trimValue(row.Sedol);
  const assetClass = trimValue(row.AssetClass);

  if (ticker) return `SecurityID ${ticker}`;
  if (isin) return `ISIN ${isin}`;
  if (sedol) return `SEDOL ${sedol}`;
  if (assetClass) return `Asset Class ${assetClass}`;
  return 'no identifier';
};

export const findSecurityForCsvRow = (
  cache: ISecurity[],
  row: CsvBasketRow,
): ISecurity | undefined => {
  const ticker = trimValue(row.SecurityID);
  const isin = trimValue(row.Isin);
  const sedol = trimValue(row.Sedol);

  if (ticker) {
    const match = findSecurityByFieldValue(cache, 'ticker', ticker);
    if (match) return match;
  }
  if (isin) {
    const match = findSecurityByFieldValue(cache, 'isin', isin);
    if (match) return match;
  }
  if (sedol) {
    const match = findSecurityByFieldValue(cache, 'sedol', sedol);
    if (match) return match;
  }
  return undefined;
};

export const matchSecurityToCsvRow = (security: ISecurity, row: CsvBasketRow): boolean =>
  matchSecurityToInput(security, toSecurityInput(row));

export const matchSecurityToInput = (security: ISecurity, input: SecurityInput): boolean => {
  if (input.ticker) {
    const candidate = fieldValue(security, 'ticker');
    if (candidate && candidate.toUpperCase() === input.ticker.toUpperCase()) return true;
  }
  if (input.cusip) {
    const candidate = security.cusip?.trim();
    if (candidate && candidate.toUpperCase() === input.cusip.toUpperCase()) return true;
  }
  if (input.isin) {
    const candidate = fieldValue(security, 'isin');
    if (candidate && candidate.toUpperCase() === input.isin.toUpperCase()) return true;
  }
  if (input.sedol) {
    const candidate = fieldValue(security, 'sedol');
    if (candidate && candidate.toUpperCase() === input.sedol.toUpperCase()) return true;
  }
  return false;
};

export const isValidCsvTradeFields = (row: CsvBasketRow): boolean => {
  const price = Number(row.Price);
  const quantity = Number(row.Quantity);
  const dividendRate = Number(row.DivRate);

  return (
    Number.isFinite(price) &&
    price > 0 &&
    Number.isFinite(quantity) &&
    quantity > 0 &&
    Number.isFinite(dividendRate) &&
    dividendRate > 0
  );
};

export const partitionCsvRowsForUpload = (
  csvRows: CsvBasketRow[],
  cache: ISecurity[],
): {
  cacheResolved: Map<number, ISecurity>;
  pendingApi: Map<string, PendingApiSecurity>;
  skipped: SkippedUploadRow[];
} => {
  const cacheResolved = new Map<number, ISecurity>();
  const pendingApi = new Map<string, PendingApiSecurity>();
  const skipped: SkippedUploadRow[] = [];

  csvRows.forEach((row, index) => {
    const rowNumber = index + 1;
    const label = getCsvRowIdentifierLabel(row);

    if (!isValidCsvTradeFields(row)) {
      skipped.push({
        rowNumber,
        label,
        reason: 'invalid price, quantity, or dividend rate',
      });
      return;
    }

    const cached = findSecurityForCsvRow(cache, row);
    if (cached?.bnySecurityId) {
      cacheResolved.set(index, cached);
      return;
    }

    const input = toSecurityInput(row);
    if (!hasAnyIdentifier(input)) {
      skipped.push({
        rowNumber,
        label,
        reason: 'no security identifier',
      });
      return;
    }

    const key = buildSecurityInputKey(input);
    if (!pendingApi.has(key)) {
      pendingApi.set(key, { input, sampleRow: row });
    }
  });

  return { cacheResolved, pendingApi, skipped };
};

export const buildApiResolvedMap = (
  incoming: ISecurity[],
  pendingApi: Map<string, PendingApiSecurity>,
): Map<string, ISecurity> => {
  const resolved = new Map<string, ISecurity>();

  pendingApi.forEach((pending, key) => {
    const match = incoming.find(
      (security) => security?.bnySecurityId && matchSecurityToInput(security, pending.input),
    );
    if (match) {
      resolved.set(key, match);
    }
  });

  return resolved;
};

export const mapCsvRowToBasketSecurity = (
  csvRow: CsvBasketRow,
  security: ISecurity,
  context: BasketUploadRowContext,
  rowId: string,
) => {
  const price = Number(csvRow.Price);
  const quantity = Number(csvRow.Quantity);
  const dividendRate = Number(csvRow.DivRate);
  const csvFxRate = Number(csvRow.FxRate);
  const enriched = mapSecurityToEnrichedFields(security, {
    swapCurrency: context.swapCurrency,
    existingFxRate: Number.isFinite(csvFxRate) && csvFxRate > 0 ? csvFxRate : undefined,
  });
  const fxRate = enriched.fxRate ?? 0;

  return {
    id: rowId,
    ticker: enriched.ticker,
    price,
    quantity,
    currency: enriched.currency,
    swapCurrency: context.swapCurrency,
    fxRate,
    dividendRate,
    notional: (price ?? 0) * (quantity ?? 0) * (fxRate ?? 1),
    fund: context.fund,
    fundId: context.fundId,
    isNewlyAdded: true,
    showAdd: false,
    securityName: enriched.securityName,
    securityDescription: enriched.securityDescription,
    isin: enriched.isin,
    sedol: enriched.sedol,
    cusip: enriched.cusip,
    security: enriched.bnySecurityId,
    bnySecurityId: enriched.bnySecurityId,
    assetClass: enriched.assetClass,
    tradeDate: context.tradeDate,
  };
};

export const buildUploadBasketRows = (
  csvRows: CsvBasketRow[],
  cacheResolved: Map<number, ISecurity>,
  apiResolved: Map<string, ISecurity>,
  context: BasketUploadRowContext,
  createRowId: () => string,
): { rows: ReturnType<typeof mapCsvRowToBasketSecurity>[]; skipped: SkippedUploadRow[] } => {
  const rows: ReturnType<typeof mapCsvRowToBasketSecurity>[] = [];
  const skipped: SkippedUploadRow[] = [];

  csvRows.forEach((row, index) => {
    const rowNumber = index + 1;
    const label = getCsvRowIdentifierLabel(row);

    if (!isValidCsvTradeFields(row)) {
      return;
    }

    let security = cacheResolved.get(index);
    if (!security) {
      const key = buildSecurityInputKey(toSecurityInput(row));
      security = apiResolved.get(key);
    }

    if (!security?.bnySecurityId) {
      skipped.push({
        rowNumber,
        label,
        reason: 'could not be resolved',
      });
      return;
    }

    const mappedRow = mapCsvRowToBasketSecurity(row, security, context, createRowId());
    if (!mappedRow.fxRate || mappedRow.fxRate <= 0) {
      skipped.push({
        rowNumber,
        label,
        reason: 'invalid fx rate',
      });
      return;
    }

    rows.push(mappedRow);
  });

  return { rows, skipped };
};

export const formatSkippedUploadMessage = (skipped: SkippedUploadRow[], maxRows = 5): string => {
  if (skipped.length === 0) return '';

  const preview = skipped.slice(0, maxRows).map((row) => `row ${row.rowNumber} (${row.label})`);
  const remainder = skipped.length > maxRows ? ` and ${skipped.length - maxRows} more` : '';

  return `${skipped.length} securities could not be imported and were skipped: ${preview.join(', ')}${remainder}`;
};
